import { FC, Ref, useRef } from "react";

import { COURSE_GROUPING_ID_QUERY_PARAM } from "components/constants";
import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "components/menus/SidebarOptionsMenu";
import { CourseGroupingType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { MenuInstance, MenuItem } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { PencilEditIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCoursesByGroupingId } from "store/selectors/section";
import { toastSuccessMessage } from "utils/alerts";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";
import { useQueryParamsManager } from "utils/routing";

interface CourseGroupingOptionsMenuProps extends SidebarOptionsMenuProps {
  courseGrouping: CourseGroupingType;
  onRename: () => void;
}

export const CourseGroupingOptionsMenu: FC<CourseGroupingOptionsMenuProps> = ({
  courseGrouping,
  onRename,
  className,
}) => {
  const menuRef: Ref<MenuInstance> = useRef(null);
  const courses = useAppSelector((state) => selectCoursesByGroupingId(state, courseGrouping.id));
  const { deleteQueryParams } = useQueryParamsManager();
  const hasCourses = courses.length > 0;

  const handleDelete = async () => {
    if (hasCourses) {
      return;
    }

    const didDelete = await showConfirmationModal({
      title: t("confirm_delete_modal.delete_item", { item: t("glossary.course_grouping") }),
      labelText: t("confirm_delete_modal.delete_label", { name: courseGrouping.title }),
    });

    if (didDelete) {
      await storeApi.course_groupings.destroy(courseGrouping.id);

      toastSuccessMessage(t("home.toasts.grouping_deleted"));

      deleteQueryParams([COURSE_GROUPING_ID_QUERY_PARAM]);
    }
  };

  return (
    <SidebarOptionsMenu
      className={className}
      eventPrefix={ADMIN_COURSE_EVENT_PREFIX}
      instanceRef={menuRef}
      kind="gray"
      optionsMenuActions={{
        onDelete: handleDelete,
        isDeleteDisabled: hasCourses,
        deleteTooltipText: hasCourses
          ? t("sidebar_options_menu.cannot_delete_grouping_with_courses")
          : undefined,
      }}
      size="s"
    >
      <MenuItem onClick={onRename}>
        <IconText
          iconStart={<PencilEditIcon />}
          text={t("sidebar_options_menu.rename_course_grouping")}
        />
      </MenuItem>
    </SidebarOptionsMenu>
  );
};
